import { compressUploadImage } from "./compressUploadImage";

/**
 * Hero banner image helpers (storefront <img> sources + Studio upload profile).
 *
 * Upload profile (browser side, before the file is sent): WebP, quality ~75, <= 200 KB,
 * desktop banner max 1920px wide, mobile banner max 800px wide (a 390px phone at 2x DPR needs ~780px).
 */
export const HERO_DESKTOP_MAX_WIDTH = 1920;
export const HERO_MOBILE_MAX_WIDTH = 800;
export const HERO_UPLOAD_QUALITY = 0.75;
export const HERO_UPLOAD_MAX_BYTES = 200 * 1024;

/**
 * @param {File} file
 * @param {"desktop" | "mobile"} kind
 * @returns {Promise<File>}
 */
export function compressHeroImageFile(file, kind = "desktop") {
  const maxWidth = kind === "mobile" ? HERO_MOBILE_MAX_WIDTH : HERO_DESKTOP_MAX_WIDTH;
  return compressUploadImage(file, {
    maxEdge: kind === "mobile" ? maxWidth * 3 : maxWidth,
    maxWidth,
    quality: HERO_UPLOAD_QUALITY,
    maxBytes: HERO_UPLOAD_MAX_BYTES,
  });
}

/**
 * `src` / `srcSet` / `sizes` for a hero <img>. The SAME object feeds the <img> and the
 * <link rel="preload" as="image"> (href / imagesrcset / imagesizes), so the browser resolves both to the
 * same candidate and fetches it once.
 *
 * Only one file exists per hero slot today (the mobile slot holds the ~800px upload, the desktop slot the
 * ~1920px one), so there is one candidate per slot; extra `variants` ({ url, width }) are used as soon as
 * a store has them, with no change needed here.
 *
 * @param {string} url
 * @param {"desktop" | "mobile" | "all"} viewport
 * @param {{ url: string, width: number }[]} [variants]
 */
export function heroImageSources(url, viewport = "all", variants) {
  const src = String(url || "").trim();
  const out = { src, srcSet: undefined, sizes: undefined };
  // srcset is comma/space separated: a URL containing either can't be expressed safely -> plain src.
  const safe = (u) => u && !/[\s,]/.test(u);
  if (!safe(src)) return out;
  const list = (Array.isArray(variants) ? variants : []).filter((v) => v && safe(v.url) && v.width > 0);
  if (!list.some((v) => v.url === src)) {
    list.push({ url: src, width: viewport === "mobile" ? HERO_MOBILE_MAX_WIDTH : HERO_DESKTOP_MAX_WIDTH });
  }
  list.sort((a, b) => a.width - b.width);
  out.srcSet = list.map((v) => `${v.url} ${v.width}w`).join(", ");
  out.sizes = "100vw"; // hero layers are full-bleed
  return out;
}
